import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Typography, Tooltip, Spin } from 'antd';
import apiClient from '../../../api/client';

const { Text } = Typography;

const PRESETS = {
    sunset: { name: 'Sunset', stops: [{ pos: 0, color: '#1a0533' }, { pos: 0.33, color: '#c2185b' }, { pos: 0.66, color: '#ff6d00' }, { pos: 1, color: '#ffd600' }] },
    neon: { name: 'Neon', stops: [{ pos: 0, color: '#0d0221' }, { pos: 0.25, color: '#0a7e8c' }, { pos: 0.5, color: '#9b59b6' }, { pos: 0.75, color: '#e91e63' }, { pos: 1, color: '#00e5ff' }] },
    vintage: { name: 'Vintage', stops: [{ pos: 0, color: '#2c1810' }, { pos: 0.33, color: '#8b6914' }, { pos: 0.66, color: '#d4a574' }, { pos: 1, color: '#f5e6d3' }] },
    cyanotype: { name: 'Cyanotype', stops: [{ pos: 0, color: '#001f3f' }, { pos: 0.33, color: '#0074D9' }, { pos: 0.66, color: '#7FDBFF' }, { pos: 1, color: '#f0f8ff' }] },
    duotone: { name: 'Duotone BW', stops: [{ pos: 0, color: '#000000' }, { pos: 1, color: '#ffffff' }] },
    heat: { name: 'Heat Map', stops: [{ pos: 0, color: '#000033' }, { pos: 0.25, color: '#3300cc' }, { pos: 0.5, color: '#ff0000' }, { pos: 0.75, color: '#ffaa00' }, { pos: 1, color: '#ffffff' }] },
    forest: { name: 'Forest', stops: [{ pos: 0, color: '#0a1a0a' }, { pos: 0.4, color: '#1b4332' }, { pos: 0.7, color: '#52b788' }, { pos: 1, color: '#d8f3dc' }] },
    candy: { name: 'Candy', stops: [{ pos: 0, color: '#2d1b69' }, { pos: 0.33, color: '#ee5a9b' }, { pos: 0.66, color: '#f9c74f' }, { pos: 1, color: '#f8f9fa' }] },
};

/**
 * GradientMapEditorPanel — Maps image luminosity to a color gradient.
 */
const GradientMapEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    const [stops, setStops] = useState([
        { pos: 0, color: '#000000' },
        { pos: 1, color: '#ffffff' },
    ]);
    const [intensity, setIntensity] = useState(1.0);
    const [luminanceMethod, setLuminanceMethod] = useState('perceived');
    const [activePreset, setActivePreset] = useState('duotone');
    const [userPalettes, setUserPalettes] = useState([]);
    const [palettesLoading, setPalettesLoading] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => setLoading(false);
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    // Fetch user palettes
    useEffect(() => {
        if (!visible) return;
        const fetchPalettes = async () => {
            setPalettesLoading(true);
            try {
                let response = await apiClient.get('/color-palettes', {
                    params: canvasId ? { canvasId } : {}
                });
                if (response.data.length === 0 && canvasId) {
                    response = await apiClient.get('/color-palettes');
                }
                setUserPalettes(response.data);
            } catch (e) {
                console.error('Failed to fetch palettes for gradient map:', e);
            } finally {
                setPalettesLoading(false);
            }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderGradientMap();
    }, [visible, stops, intensity, luminanceMethod, loading]);

    const renderGradientMap = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    preview.width = ev.data.width; preview.height = ev.data.height;
                    preview.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderGradientMap(); }
            };

            worker.postMessage({
                type: 'gradientMap', pixels: srcBuf, width: w, height: h,
                stops, intensity, luminanceMethod, generation,
            }, [srcBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(previewCanvasRef.current, 800); }
                finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderGradientMap(); }
                }
            });
        }
    }, [stops, intensity, luminanceMethod]);

    const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return [parseInt(h.substring(0, 2), 16) || 0, parseInt(h.substring(2, 4), 16) || 0, parseInt(h.substring(4, 6), 16) || 0];
    };

    // Build a 256-entry LUT from gradient stops
    const buildLUT = () => {
        const sorted = [...stops].sort((a, b) => a.pos - b.pos);
        const lut = new Array(256);
        for (let i = 0; i < 256; i++) {
            const t = i / 255;
            // Find surrounding stops
            let lo = sorted[0], hi = sorted[sorted.length - 1];
            for (let s = 0; s < sorted.length - 1; s++) {
                if (t >= sorted[s].pos && t <= sorted[s + 1].pos) {
                    lo = sorted[s]; hi = sorted[s + 1]; break;
                }
            }
            const range = hi.pos - lo.pos;
            const localT = range === 0 ? 0 : (t - lo.pos) / range;
            const loRgb = hexToRgb(lo.color);
            const hiRgb = hexToRgb(hi.color);
            lut[i] = [
                Math.round(loRgb[0] + (hiRgb[0] - loRgb[0]) * localT),
                Math.round(loRgb[1] + (hiRgb[1] - loRgb[1]) * localT),
                Math.round(loRgb[2] + (hiRgb[2] - loRgb[2]) * localT),
            ];
        }
        return lut;
    };

    const doRender = (canvas, maxDim) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;
        const lut = buildLUT();

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            let lum;
            if (luminanceMethod === 'perceived') {
                lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
            } else if (luminanceMethod === 'average') {
                lum = Math.round((r + g + b) / 3);
            } else {
                lum = Math.max(r, g, b);
            }
            lum = Math.max(0, Math.min(255, lum));

            const mapped = lut[lum];
            // Blend original with mapped
            data[i] = Math.round(r + (mapped[0] - r) * intensity);
            data[i + 1] = Math.round(g + (mapped[1] - g) * intensity);
            data[i + 2] = Math.round(b + (mapped[2] - b) * intensity);
            // Alpha preserved from original
        }

        ctx.putImageData(imageData, 0, 0);
    };

    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const w = img.width, h = img.height;

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            tmpCtx.drawImage(img, 0, 0, w, h);
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outCanvas = document.createElement('canvas');
                outCanvas.width = ev.data.width; outCanvas.height = ev.data.height;
                outCanvas.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'gradientMap', pixels: srcBuf, width: w, height: h,
                stops, intensity, luminanceMethod, generation,
            }, [srcBuf]);
        } else {
            const fullCanvas = document.createElement('canvas');
            doRender(fullCanvas, Math.max(img.width, img.height));
            onApply?.(fullCanvas.toDataURL('image/png'));
        }
    }, [stops, intensity, luminanceMethod, onApply]);

    const applyPreset = (key) => {
        setActivePreset(key);
        setStops(PRESETS[key].stops.map(s => ({ ...s })));
    };

    const applyPalette = (palette) => {
        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color);
        if (colors.length === 0) return;
        setActivePreset(null);
        if (colors.length === 1) {
            setStops([{ pos: 0, color: colors[0] }, { pos: 1, color: colors[0] }]);
        } else {
            setStops(colors.map((color, i) => ({
                pos: i / (colors.length - 1),
                color,
            })));
        }
    };

    // CSS gradient string for preview
    const gradientCSS = [...stops].sort((a, b) => a.pos - b.pos)
        .map(s => `${s.color} ${Math.round(s.pos * 100)}%`).join(', ');

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#f472b6' }, rail: { background: '#30363d' }, handle: { borderColor: '#f472b6', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#f472b6', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Preview */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d' }} />
                )}
            </div>

            {/* Controls */}
            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>▰</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Gradient Map</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Gradient Preview */}
                    <div>
                        <Text style={{ color: '#f472b6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Gradient</Text>
                        <div style={{
                            marginTop: 8, height: 28, borderRadius: 6, border: '1px solid #30363d',
                            background: `linear-gradient(to right, ${gradientCSS})`,
                        }} />
                        {/* Color stops */}
                        <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {stops.map((s, i) => (
                                <Tooltip key={i} title={`${s.color} @ ${Math.round(s.pos * 100)}%`}>
                                    <div
                                        style={{ width: 28, height: 28, borderRadius: 4, background: s.color, border: '1px solid #30363d', cursor: 'pointer', position: 'relative' }}
                                        onClick={() => {
                                            const input = document.createElement('input');
                                            input.type = 'color'; input.value = s.color;
                                            input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                                            document.body.appendChild(input);
                                            input.addEventListener('input', (e) => {
                                                setStops(prev => { const n = [...prev]; n[i] = { ...n[i], color: e.target.value }; return n; });
                                            });
                                            input.addEventListener('change', () => document.body.removeChild(input));
                                            input.click();
                                        }}
                                    />
                                </Tooltip>
                            ))}
                            {stops.length < 6 && (
                                <div
                                    style={{ width: 28, height: 28, borderRadius: 4, border: '1px dashed #30363d', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6e7681', fontSize: 16 }}
                                    onClick={() => setStops(prev => [...prev, { pos: 0.5, color: '#888888' }].sort((a, b) => a.pos - b.pos))}
                                >+</div>
                            )}
                        </div>
                        {/* Position sliders for each stop */}
                        <div style={{ marginTop: 8 }}>
                            {stops.map((s, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                    <div style={{ width: 12, height: 12, borderRadius: 2, background: s.color, border: '1px solid #30363d', flexShrink: 0 }} />
                                    <Slider min={0} max={1} step={0.01} value={s.pos}
                                        onChange={(v) => setStops(prev => { const n = [...prev]; n[i] = { ...n[i], pos: v }; return n; })}
                                        style={{ flex: 1, margin: 0 }}
                                        styles={{ track: { background: '#f472b6' }, rail: { background: '#30363d' }, handle: { borderColor: '#f472b6', width: 10, height: 10 } }}
                                    />
                                    <Text style={{ color: '#6e7681', fontSize: 10, minWidth: 28 }}>{Math.round(s.pos * 100)}%</Text>
                                    {stops.length > 2 && (
                                        <span style={{ cursor: 'pointer', color: '#484f58', fontSize: 10 }}
                                            onClick={() => setStops(prev => prev.filter((_, idx) => idx !== i))}>✕</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Presets */}
                    <div>
                        <Text style={{ color: '#f472b6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Presets</Text>
                        <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {Object.entries(PRESETS).map(([key, preset]) => {
                                const css = preset.stops.map(s => `${s.color} ${Math.round(s.pos * 100)}%`).join(', ');
                                return (
                                    <Tooltip key={key} title={preset.name}>
                                        <div
                                            onClick={() => applyPreset(key)}
                                            style={{
                                                width: 36, height: 24, borderRadius: 4, cursor: 'pointer',
                                                background: `linear-gradient(to right, ${css})`,
                                                border: activePreset === key ? '2px solid #f472b6' : '1px solid #30363d',
                                                transition: 'all 0.15s ease',
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>

                    {/* User Palettes */}
                    {userPalettes.length > 0 && (
                        <div>
                            <Text style={{ color: '#f472b6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Your Palettes</Text>
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {palettesLoading ? <Spin size="small" /> : userPalettes.map((palette) => {
                                    const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color);
                                    if (colors.length === 0) return null;
                                    const gradCSS = colors.length === 1
                                        ? colors[0]
                                        : colors.map((c, i) => `${c} ${Math.round((i / (colors.length - 1)) * 100)}%`).join(', ');
                                    return (
                                        <div
                                            key={palette.id || palette.name}
                                            onClick={() => applyPalette(palette)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '6px 8px',
                                                borderRadius: 6,
                                                border: '1px solid #30363d',
                                                background: '#161b22',
                                                transition: 'all 0.15s ease',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f472b6'; e.currentTarget.style.background = '#1c2333'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.background = '#161b22'; }}
                                        >
                                            <Text style={{ color: '#c9d1d9', fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 5 }}>{palette.name}</Text>
                                            <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
                                                {colors.map((c, i) => (
                                                    <div key={i} style={{
                                                        width: 16, height: 16, borderRadius: 3,
                                                        background: c, border: '1px solid rgba(255,255,255,0.1)',
                                                        flexShrink: 0,
                                                    }} />
                                                ))}
                                            </div>
                                            <div style={{
                                                height: 4, borderRadius: 2,
                                                background: colors.length === 1 ? colors[0] : `linear-gradient(to right, ${gradCSS})`,
                                            }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Settings */}
                    <div>
                        <Text style={{ color: '#f472b6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Settings</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Intensity', 0, 1, intensity, (v) => setIntensity(v), 0.01)}
                        </div>
                        <Select value={luminanceMethod} onChange={setLuminanceMethod} style={{ width: '100%', marginTop: 6 }}
                            options={[
                                { value: 'perceived', label: 'Perceived Luminance' },
                                { value: 'average', label: 'Average' },
                                { value: 'max', label: 'Max Channel' },
                            ]}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#f472b6', borderColor: '#f472b6' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default GradientMapEditorPanel;
